import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import type { Payment, PaymentAllocation } from '@data/db/types';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { cancelDocument, voidAllocationsForDocument } from '@data/services/paymentService';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { DOCUMENTS_QUERY_ROOT } from '@features/documents/useDocuments';
import { PAYMENTS_QUERY_ROOT } from './useReceipts';

const USER = { id: 'local', name: 'You' };

export interface InvoicePaymentHistoryRow {
  readonly payment: Payment | null;
  readonly allocation: PaymentAllocation;
}

export function useInvoicePaymentHistory(documentId: string | null) {
  const business = useQuery({
    queryKey: [...PAYMENTS_QUERY_ROOT, 'active-business'],
    queryFn: async () => resolveActiveBusiness(getDb() as never),
  });
  return useQuery({
    queryKey: [...PAYMENTS_QUERY_ROOT, 'invoice-history', business.data?.id, documentId],
    enabled: !!business.data?.id && !!documentId,
    queryFn: async () => {
      const db = getDb();
      const document = await db.documents.get(documentId as string);
      if (!document || document.businessId !== business.data?.id) {
        throw new Error('This invoice does not belong to the active business.');
      }
      const allocations = await db.paymentAllocations.where('documentId').equals(document.id).toArray();
      const rows: InvoicePaymentHistoryRow[] = [];
      for (const allocation of allocations.sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
        const payment = await db.payments.get(allocation.paymentId);
        if (payment?.businessId === document.businessId || allocation.voidedAt) {
          rows.push({ payment: payment?.businessId === document.businessId ? payment : null, allocation });
        }
      }
      return rows;
    },
  });
}

export function useCancelUnpaidInvoice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { documentId: string; reason: string }) => {
      const db = getDb();
      const deviceId = await currentDeviceId(db);
      const now = new Date().toISOString();
      return db.transaction(
        'rw',
        [db.documents, db.paymentAllocations, db.payments, db.auditLogs, db.syncOutbox],
        () => cancelDocument(db as never, input.documentId, input.reason, USER, now, deviceId),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT }),
        client.invalidateQueries({ queryKey: PAYMENTS_QUERY_ROOT }),
      ]);
    },
  });
}

export function useReverseInvoiceAllocations() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { documentId: string; reason: string }) => {
      const db = getDb();
      const deviceId = await currentDeviceId(db);
      const now = new Date().toISOString();
      return db.transaction(
        'rw',
        [db.documents, db.paymentAllocations, db.payments, db.auditLogs, db.syncOutbox],
        () => voidAllocationsForDocument(db as never, input.documentId, input.reason, USER, now, deviceId),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT }),
        client.invalidateQueries({ queryKey: PAYMENTS_QUERY_ROOT }),
      ]);
    },
  });
}
